import {
  SearchEmptyState,
  SearchHiddenCount,
  SearchInput,
} from '#src/pages/group/choose_name_card/search_names.tsx';
import { DisplayGroupName } from '#src/ui/presentation/display_group_name.tsx';
import { DisplayParticipantName } from '#src/ui/presentation/display_participant_name.tsx';
import type { GroupParticipant } from '@packages/contracts/group';
import { Button } from '@packages/ui/base/button/button';
import { Card, CardLogo } from '@packages/ui/base/card/card';
import { fuzzySearch } from '@packages/ui/search/fuzzy_search';
import { useMemo, useState } from 'react';

/** Minimum number of participants before showing the search input. */
const SEARCH_VISIBILITY_THRESHOLD = 8;

export interface ChooseNameCardProps {
  groupName: string;
  participants: Pick<GroupParticipant, 'id' | 'name'>[];
  onChooseParticipant: (id: string) => void;
}

export const ChooseNameCard = ({
  groupName,
  participants,
  onChooseParticipant,
}: ChooseNameCardProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const showSearch = participants.length >= SEARCH_VISIBILITY_THRESHOLD;

  const filteredParticipants = useMemo(
    () => fuzzySearch(participants, searchQuery, p => p.name),
    [participants, searchQuery],
  );

  const hiddenCount = participants.length - filteredParticipants.length;
  const hasQuery = searchQuery.trim().length > 0;

  const clearSearch = () => setSearchQuery('');

  return (
    <Card className="flex max-h-full flex-col overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Logo */}
        <CardLogo className="mb-4" />

        <div className="animate-in fade-in flex min-h-0 flex-1 flex-col">
          {/* Group Name */}
          <h1 className="mb-0.5 text-xl font-bold">
            <DisplayGroupName name={groupName} />
          </h1>

          {/* Subtitle */}
          <div className="text-muted-foreground mb-4">Choose your name to get started.</div>

          {/* Search */}
          {showSearch && (
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search names…"
              className="mb-2"
            />
          )}

          {/* Names */}
          <div className="min-h-0 flex-1 overflow-y-auto pt-2">
            {hasQuery && filteredParticipants.length === 0 ? (
              <SearchEmptyState onShowAll={clearSearch} />
            ) : (
              <NameList
                participants={filteredParticipants}
                onChooseParticipant={onChooseParticipant}
              />
            )}

            {hasQuery && hiddenCount > 0 && filteredParticipants.length > 0 && (
              <SearchHiddenCount hiddenCount={hiddenCount} onShowAll={clearSearch} />
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

interface NameListProps {
  participants: Pick<GroupParticipant, 'id' | 'name'>[];
  onChooseParticipant: (id: string) => void;
}

const NameList = ({ participants, onChooseParticipant }: NameListProps) => {
  return (
    <div className="space-y-4">
      {participants.map(participant => (
        <Button
          key={participant.id}
          className="block w-full"
          onClick={() => onChooseParticipant(participant.id)}
        >
          <span className="truncate">
            <DisplayParticipantName name={participant.name} />
          </span>
        </Button>
      ))}
    </div>
  );
};
